import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { getViewModel$ } from '../viewModel$/viewModel$';
import { DependencyMatrixViewModel } from '../types';
import { DependencyMatrix } from './DependencyMatrix';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { CONTENT_PANE_CLASSNAME } from '../consts';
import { getLeftMenuConfig } from '../getLeftMenuConfig';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { onViewSettingsUpdate } from '../../onViewSettingsUpdate';
import { getExportSettings } from '../getExportSettings';
import { SettingsBar, settingsBarConsts } from '@ardoq/settings-bar';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import {
  ViewLegend,
  getActiveConditionalFormattingForLegend,
  getComponentTypesForLegend,
} from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { ViewLegendContainer } from '@ardoq/graph';
import { ContextMenuWrapper, ExportContainer } from '../atoms';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { isPresentationMode } from 'env';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { noop } from 'lodash';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.DEPMATRIX;

const DependencyMatrixView = (props: DependencyMatrixViewModel) => {
  const {
    viewSettings,
    workspaceId,
    workspaceIds,
    workspaceNamesByWorkspaceId,
    referenceTypes,
    componentId: selectedComponentId,
    focusedComponentId,
    sourceData,
    targetData,
    sourceComponentItemDataById,
    targetComponentItemDataById,
    references,
    referenceCssClassNamesById,
    selectedReferenceTypeNameByComponentId,
    legendReferenceTypes,
    noConnectedComponents,
    isViewEmpty,
    isReadOnlyVisualization,
    errors,
    permissionContext,
    constraintEnforcementMode,
    intendedRefTypes,
    locale,
    i18nLocale,
    errorInfoBoxLabels,
  } = props;

  const viewLegendLabels = useMemo(
    () => getViewLegendLabels(i18nLocale),
    [i18nLocale]
  );

  const [isExporting, setIsExporting] = useState(false);

  const exportContainer = useRef<HTMLDivElement | null>(null);

  const [clearedErrors, setClearedErrors] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_DEPENDENCY_MATRIX
  );

  const { exportToPng, addToPresentation } = useMemo(
    () => getExportSettings(VIEW_ID, exportContainer, i18nLocale),
    [i18nLocale]
  );
  const startExporting = useCallback(() => setIsExporting(true), []);
  const exportTableToPng = useCallback(async () => {
    try {
      await exportToPng();
    } finally {
      setIsExporting(false);
    }
  }, [exportToPng]);

  // Exporting a table to a PNG image after the table has rendered all the data
  useEffect(() => {
    if (isExporting) {
      exportTableToPng();
    }
  }, [isExporting, exportTableToPng]);

  const heightOffset = useViewLegendSubscription();

  const hasNoWriteAccess = !workspaceAccessControlInterface.canEditWorkspace(
    permissionContext,
    workspaceId,
    getActiveScenarioState()
  );

  const presentationMode = isPresentationMode();

  return (
    <ContextMenuWrapper data-context-menu={OBJECT_CONTEXT_MENU_NAME}>
      {!presentationMode && (
        <SettingsBar
          viewId={VIEW_ID}
          i18nLocale={i18nLocale}
          leftMenu={getLeftMenuConfig({
            viewId: VIEW_ID,
            viewState: viewSettings,
            workspaceId,
            workspaceIds,
            workspaceNamesByWorkspaceId,
            referenceTypes,
            locale,
            i18nLocale,
          })}
          rightMenu={getRightMenuConfig({
            isViewpointMode: false,
            viewId: VIEW_ID,
            viewstate: viewSettings,
            onViewSettingsUpdate,
            knowledgeBaseLink: KnowledgeBaseLink.DEPMATRIX,
            exports: {
              exportToPng: startExporting,
              addToPresentation,
              isDisabled: isViewEmpty,
            },
            i18nLocale,
          })}
        />
      )}

      {isViewEmpty ? (
        <EmptyStateBase noConnectedComponents={noConnectedComponents} />
      ) : (
        <ExportContainer
          $isExporting={isExporting}
          className={CONTENT_PANE_CLASSNAME}
          ref={exportContainer}
        >
          <ErrorInfoBox
            errors={clearedErrors ? [] : errors}
            hasClones={false}
            clearHasClones={noop}
            isShowNeverAgainSet={neverShowAgain}
            isPresentationMode={presentationMode}
            clearErrors={() => setClearedErrors(true)}
            toggleNeverShowAgain={toggleNeverShowAgain}
            {...errorInfoBoxLabels}
          />
          <DependencyMatrix
            readOnly={
              hasNoWriteAccess || presentationMode || isReadOnlyVisualization
            }
            references={references}
            sourceData={sourceData}
            targetData={targetData}
            sourceComponentItemDataById={sourceComponentItemDataById}
            targetComponentItemDataById={targetComponentItemDataById}
            referenceCssClassNamesById={referenceCssClassNamesById}
            selectedReferenceTypeNameByComponentId={
              selectedReferenceTypeNameByComponentId
            }
            viewSettings={viewSettings}
            isExporting={isExporting}
            workspaceId={workspaceId}
            selectedComponentId={selectedComponentId}
            focusedComponentId={focusedComponentId}
            constraintEnforcementMode={constraintEnforcementMode}
            intendedRefTypes={intendedRefTypes}
          />
          <ViewLegendContainer
            heightOffset={heightOffset}
            visible={viewSettings[settingsBarConsts.IS_LEGEND_ACTIVE]}
            style={isExporting ? { position: 'static' } : {}}
          >
            <ViewLegend
              activeDiffMode={null}
              componentTypes={getComponentTypesForLegend(
                sourceData.componentIds
              )}
              referenceTypes={legendReferenceTypes}
              activeConditionalFormatting={getActiveConditionalFormattingForLegend(
                i18nLocale
              )}
              labels={viewLegendLabels}
            />
          </ViewLegendContainer>
        </ExportContainer>
      )}
    </ContextMenuWrapper>
  );
};

const ConnectedDependencyMatrix = connectInstanceWithProps(
  DependencyMatrixView,
  getViewModel$
);

export { ConnectedDependencyMatrix };
